 

import React, {
  useState,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Button, Row, Col, Label } from 'reactstrap';

type BeneficiaryData = {
  account: string;
  weight: string;
};

type AdvancedOptions = {
  rewards: string;
  beneficiaries: BeneficiaryData[];
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
  options: AdvancedOptions;
  setOptions: (options: AdvancedOptions) => void;
  orientation: "clip" | "video";
  setOrientation: (val: "clip" | "video") => void;
  supportSpeak: boolean;
  author: string;
  appSettings: any;
};

const AdvancedSettings = forwardRef((props: Props, ref) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  // Focus states for premium UI
  const [focRewards, setFocRewards] = useState(false);
  const [focBeneficiaries, setFocBeneficiaries] = useState<{[key: number]: boolean}>({});
  const [focAmount, setFocAmount] = useState<{[key: number]: boolean}>({});

  const toggleAdvanced = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowAdvanced((prev) => !prev);
  };

  const calculateAuthorWeight = () => {
      if (!props.options || !Array.isArray(props.options.beneficiaries)) return props.supportSpeak ? 90 : 100;
      const speemWeight = props.supportSpeak ? 10 : 0;
      const totalBeneficiaries = props.options.beneficiaries.reduce((sum, b) => {
          const w = parseFloat(b.weight || '0');
          return sum + (isNaN(w) ? 0 : w);
      }, 0);
      return Math.max(0, 100 - speemWeight - totalBeneficiaries);
  };

  const handleAddBeneficiary = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentAuthor = calculateAuthorWeight();
    if (currentAuthor <= 0) {
        setToastMessage('Maximum percentage (100%) already reached.');
        setTimeout(() => setToastMessage(null), 3000);
        return;
    }

    if (props.options.beneficiaries.length >= 8) {
      setToastMessage('You can add up to 8 beneficiaries.');
      setTimeout(() => setToastMessage(null), 3000);
      return;
    }

    props.setOptions({
        ...props.options,
        beneficiaries: [
            ...props.options.beneficiaries,
            { account: '', weight: '' }
        ]
    });
  };

  const handleRemoveBeneficiary = (index: number) => {
      props.setOptions({
          ...props.options,
          beneficiaries: props.options.beneficiaries.filter((_, i) => i !== index)
      });
  };

  const updateBeneficiary = (index: number, field: keyof BeneficiaryData, value: string) => {
      const newBeneficiaries = [...props.options.beneficiaries];
      
      if (field === 'weight') {
          // Calculate available weight
          const otherBeneficiaries = props.options.beneficiaries.reduce((sum, b, i) => {
              if (i === index) return sum;
              const w = parseFloat(b.weight || '0');
              return sum + (isNaN(w) ? 0 : w);
          }, 0);
          const speemWeight = props.supportSpeak ? 10 : 0;
          const maxAvailable = 100 - speemWeight - otherBeneficiaries;
          
          let numVal = parseFloat(value);
          if (numVal > maxAvailable) value = maxAvailable.toString();
          if (numVal < 0) value = '0';
      }

      newBeneficiaries[index] = { ...newBeneficiaries[index], [field]: value };
      props.setOptions({
          ...props.options,
          beneficiaries: newBeneficiaries
      });
  };

  const updateRewards = (value: string) => {
      props.setOptions({
          ...props.options,
          rewards: value
      });
  };

  const getOptionsComment = () => {
    const beneficiaryList = props.options.beneficiaries
      .map((b) => {
        const account = b.account.trim().toLowerCase().replace(/@/g, '');
        const weight = parseFloat(b.weight || '0') * 100;

        if (!account || isNaN(weight) || weight <= 0) return null;

        return {
          account,
          weight: Math.min(Math.max(weight, 0), 10000)
        };
      })
      .filter(Boolean); 
    return {
      rewards: props.options.rewards,
      beneficiaries: beneficiaryList
    };
  };

  useImperativeHandle(ref, () => ({
    getOptionsComment
  }));

  const authorWeight = calculateAuthorWeight();

  return (
    <Row className={props.className} style={props.style}>
      <Col md="12">
        <Label className='mb-0 d-flex align-items-center' style={{ cursor: 'pointer' }} onClick={toggleAdvanced}>
          <a href="#" className="advanced-button" onClick={(e) => e.preventDefault()}>
            3. Advanced Settings
          </a>
          <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'} ml-2 text-muted`} style={{ fontSize: '12px' }} />
        </Label>
      </Col>

      {showAdvanced && (
        <>
          <Col md="12" className="mt-2">
              <Row className="align-items-end">
                  <Col md="8">
                    <Label className="small text-muted mb-1">Rewards & Video Orientation</Label> 
                    <div className="d-flex align-items-center gap-2">
                        <div className={`input-icon-group flex-grow-1 ${focRewards ? "focused" : ""}`}>
                            <i className="fas fa-gift input-icon" />
                            <select
                                name="rewards"
                                id="rewards"
                                className="form-control fancy-input pl-2"
                                value={props.options?.rewards || '1'}
                                onChange={(e) => updateRewards(e.target.value)}
                                onFocus={() => setFocRewards(true)}
                                onBlur={() => setFocRewards(false)}
                                style={{ paddingLeft: '40px' }}
                            >
                                <option value="1">50% SBD / 50% SP</option>
                                <option value="2">100% Power Up (SP)</option>
                                <option value="3">Decline rewards</option>
                            </select>
                        </div>
                        
                        <div className="d-flex gap-1 ml-1" style={{ height: '42px' }}>
                            <div
                                className={`orientation-option d-flex align-items-center justify-content-center px-3 rounded cursor-pointer ${
                                    props.orientation === "clip" ? "checked" : "border"
                                }`}
                                onClick={() => props.setOrientation("clip")}
                                title="Portrait Orientation"
                                style={{ minWidth: '50px' }}
                            >
                                <img src={props.appSettings.app_mode === "default" ? "/assets/img/v-black.png" : "/assets/img/v-white.png"} style={{ height: '18px' }} alt="Portrait" />
                            </div>
                            <div
                                className={`orientation-option d-flex align-items-center justify-content-center px-3 rounded cursor-pointer ${
                                    props.orientation === "video" ? "checked" : "border"
                                }`}
                                onClick={() => props.setOrientation("video")}
                                title="Landscape Orientation"
                                style={{ minWidth: '50px' }}
                            >
                                <img src={props.appSettings.app_mode === "default" ? "/assets/img/h-black.png" : "/assets/img/h-white.png"} style={{ height: '18px' }} alt="Landscape" />
                            </div>
                        </div>
                    </div>
                  </Col>
              </Row>
          </Col>

          <Col md="12" className="mt-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="small text-muted mb-0">Beneficiaries List</Label>
                  <Button
                    size="sm"
                    className="btn btn-gray py-1 px-3"
                    onClick={handleAddBeneficiary}
                  >
                    <i className="fas fa-plus mr-2" />
                    Add Account
                  </Button>
              </div>

              {/* Author Row (Read-only) */}
              <Row className="mb-2 align-items-center">
                  <Col xs="7">
                      <div className="input-icon-group">
                          <i className="fas fa-user-check input-icon text-speem" style={{ borderRadius: '4px 0 0 4px' }} />
                          <input
                              type="text"
                              value={props.author}
                              readOnly
                              className="form-control fancy-input pl-2 text-muted"
                              style={{ paddingLeft: '40px' }}
                          />
                      </div>
                  </Col>
                  <Col xs="5">
                    <div className="input-icon-group">
                        <i className="fas fa-percentage input-icon text-speem" style={{ borderRadius: '4px 0 0 4px' }} />
                        <input
                            type="text"
                            value={authorWeight + '%'}
                            readOnly
                            className="form-control fancy-input pl-2 text-speem font-weight-bold"
                            style={{ background: 'var(--speem-light)', paddingLeft: '40px' }}
                        />
                    </div>
                  </Col>
              </Row>


              {/* Dynamic Beneficiaries */}
              {props.options.beneficiaries.map((b, index) => (
                <Row key={index} className="mb-2 align-items-center animate__animated animate__fadeInUp">
                  <Col xs="7">
                    <div className={`input-icon-group ${focBeneficiaries[index] ? "focused" : ""}`}>
                        <i className="fas fa-user input-icon" />
                        <input
                            type="text"
                            value={b.account}
                            onChange={(e) => updateBeneficiary(index, 'account', e.target.value)}
                            onFocus={() => setFocBeneficiaries({ ...focBeneficiaries, [index]: true })}
                            onBlur={() => setFocBeneficiaries({ ...focBeneficiaries, [index]: false })}
                            placeholder={`Account name`}
                            className="form-control fancy-input pl-2"
                            style={{ paddingLeft: '40px' }}
                        />
                    </div>
                  </Col>
                  <Col xs="5" className="d-flex align-items-center">
                    <div className={`input-icon-group ${focAmount[index] ? "focused" : ""}`}>
                        <i className="fas fa-percentage input-icon" />
                        <input
                            type="number"
                            value={b.weight}
                            onChange={(e) => updateBeneficiary(index, 'weight', e.target.value)}
                            onFocus={() => setFocAmount({ ...focAmount, [index]: true })}
                            onBlur={() => setFocAmount({ ...focAmount, [index]: false })}
                            placeholder={`1-99`}
                            className="form-control fancy-input pl-2"
                            style={{ paddingLeft: '40px' }}
                        />
                    </div>
                    <Button 
                        color="link" 
                        className="ml-2 p-0" 
                        onClick={() => handleRemoveBeneficiary(index)}
                        title="Remove"
                    >
                        <i className="fas fa-times-circle" />
                    </Button>
                  </Col>
                </Row>
              ))}
          </Col>
        </>
      )}

      {toastMessage && (
        <Col md="12" className="mt-2 rose-red small text-center">
          <i className="fas fa-exclamation-triangle mr-2" />
          {toastMessage}
        </Col>
      )}
    </Row>
  );
});

export default AdvancedSettings;
